import {
  OUTREACH_STAGE_LABELS,
  OUTREACH_TERMINAL_STAGES,
  OutreachStageSchema,
  type OutreachProspect,
} from "@lumika/shared";
import type { Admin } from "@lumika/shared";

/** Pipeline stages in funnel order, for selects and the summary strip. */
export const STAGE_ORDER = OutreachStageSchema.options;

/** Re-exported from shared so UI labels match the digest email. */
export const STAGE_LABELS = OUTREACH_STAGE_LABELS;

/** Badge tone per stage — kept to the four shared, theme-safe variants. */
export const STAGE_VARIANT: Record<
  (typeof STAGE_ORDER)[number],
  "default" | "success" | "warning" | "secondary"
> = {
  new: "secondary",
  ready_to_reach_out: "secondary",
  to_follow_up: "default",
  interested: "default",
  demo_scheduled: "warning",
  negotiating: "warning",
  won: "success",
  lost: "secondary",
};

const TERMINAL = new Set<string>(OUTREACH_TERMINAL_STAGES);

/** Whether a prospect is awaiting re-contact: non-terminal + past-due date. */
export function isDue(prospect: OutreachProspect, now: number = Date.now()): boolean {
  if (TERMINAL.has(prospect.stage)) return false;
  if (!prospect.nextFollowUpAt) return false;
  return new Date(prospect.nextFollowUpAt).getTime() <= now;
}

/** Date-only render of a follow-up timestamp (stored at 00:00:00Z). */
export function formatFollowUp(iso: string | null): string {
  if (!iso) return "—";
  return iso.slice(0, 10);
}

/** Whether a demo is scheduled for today (local calendar day). */
export function isDemoToday(iso: string | null, now: number = Date.now()): boolean {
  if (!iso) return false;
  const d = new Date(iso);
  const n = new Date(now);
  return (
    d.getFullYear() === n.getFullYear() &&
    d.getMonth() === n.getMonth() &&
    d.getDate() === n.getDate()
  );
}

/** Whether a demo's scheduled time is in the past. */
export function isDemoOverdue(iso: string | null, now: number = Date.now()): boolean {
  if (!iso) return false;
  return new Date(iso).getTime() < now;
}

/**
 * Convert a stored ISO timestamptz to the value required by an
 * `<input type="datetime-local">` (YYYY-MM-DDTHH:mm, local time, no tz suffix).
 */
export function isoToDatetimeLocal(iso: string | null): string {
  if (!iso) return "";
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return ""; // guard against an unparseable value
  // toLocaleString uses the system locale — we need a precise format.
  const pad = (n: number) => String(n).padStart(2, "0");
  return (
    `${d.getFullYear()}-${pad(d.getMonth() + 1)}-${pad(d.getDate())}` +
    `T${pad(d.getHours())}:${pad(d.getMinutes())}`
  );
}

/**
 * Convert a `datetime-local` input value (YYYY-MM-DDTHH:mm, local time)
 * to an ISO 8601 string in UTC, suitable for the API. Returns null for blank.
 */
export function datetimeLocalToIso(value: string): string | null {
  if (!value) return null;
  const d = new Date(value);
  // A partially-typed input (e.g. "2026-06") yields an Invalid Date whose
  // toISOString() would throw — treat anything unparseable as "no value".
  if (Number.isNaN(d.getTime())) return null;
  return d.toISOString();
}

export function formatDateTime(iso: string): string {
  return new Date(iso).toLocaleString(undefined, {
    dateStyle: "medium",
    timeStyle: "short",
  });
}

/** Build an admin-id → display-name lookup for owner columns/pickers. */
export function buildAdminNames(admins: Admin[]): Map<string, string> {
  return new Map(admins.map((a) => [a.id, a.displayName ?? a.email]));
}

export function ownerLabel(ownerAdminId: string | null, names: Map<string, string>): string {
  if (!ownerAdminId) return "Unassigned";
  return names.get(ownerAdminId) ?? "Unknown";
}
